import cloneDeep from 'lodash/cloneDeep';
import forOwn from 'lodash/forOwn';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import set from 'lodash/set';
import startsWith from 'lodash/startsWith';

/**
 * The current report version we are using.
 * @type {number}
 */
const CURRENT_REPORT_VERSION = 2;

/**
 * The report owner types.
 *
 * @type {Object<string, string>}
 */
const OwnerType = {
  solink: 'solink',
  cloud: 'cloud',
  reseller: 'reseller',
  customer: 'customer',
  user: 'user',
};

/**
 * Get the localized value (name/description) based on the provided locale.
 *
 * @param {Object<string, string>} localizedValues - The localized values (i.e. locale to localized text mapping)
 * @param {string} locale - The locale key
 * @returns {string}
 */
const getLocalizedValue = ({ localizedValues, locale }) => {
  let value = get(localizedValues, locale);

  if (!value && locale && locale.indexOf('-') !== -1) {
    // Try to find a similar language, e.g. 'en-UK' provided, but only 'en-CA' is available in the localized values
    const [language] = locale.split('-');
    forOwn(localizedValues, (localizedValue, localeKey) => {
      if (startsWith(localeKey, language)) {
        value = localizedValue;
        return false;
      }
    });
  }

  if (!value) {
    // Use the first available localized value
    forOwn(localizedValues, (localizedValue) => {
      value = localizedValue;
      return false;
    });
  }

  return value || '';
};

class ReportUtil {
  /**
   * Constructor
   *
   * @param {string} templateId - The report template ID
   * @param {string} templateOwnerType - The owner of the report template (and thus the report)
   * @param {string} reports - The underlying raw reports
   * @param {string} reportType - The report type
   * @param {Object} name - The localized names (titles) of the report
   * @param {Object} description - The localized descriptions of the report
   * @param {string} queryType - The query type
   * @param {Object} query - The Elasticsearch query
   * @param {Object} metadata - The report metadata (e.g. version info)
   * @param {boolean|null} isVisible - Indicates report visibility in VD
   * @param {Object} queryParams - Custom query parameters
   * @param {Object} reportSettings - The merged report settings
   * @param {Object} userNotification - The user notification config
   */
  constructor({
    templateId,
    templateOwnerType,
    reports,
    reportType,
    name,
    description,
    queryType,
    query,
    metadata,
    isVisible,
    queryParams,
    reportSettings,
    userNotification,
  }) {
    // Report template properties (defines the report)
    this.templateId = templateId;
    this.templateOwnerType = templateOwnerType;
    this.reports = reports;
    this.reportType = reportType;
    this.name = name;
    this.description = description;
    this.queryType = queryType;
    this.query = query;
    this.metadata = metadata;

    // Merged report (assignment) properties
    this.visibility = isVisible;
    this.queryParams = queryParams;
    this.reportSettings = reportSettings;

    // User notification (real-time notifications)
    this.userNotification = userNotification;

    // Report (assignments) by owner type
    this.reportAssignments = {};
  }

  /**
   * Alias for the function getTemplateId as this is what really identifies a report.
   *
   * @returns {string}
   */
  getId() {
    return this.getTemplateId();
  }

  /**
   * Get the root report (assignment) ID.
   * @returns {string}
   */
  getRootReportId() {
    return get(this.reportAssignments, [this.templateOwnerType, 'id']);
  }

  /**
   * Get the user's report (assignment) ID.
   *
   * IMPORTANT: This ID should only be used when updating custom user settings.
   *
   * @returns {string|undefined}
   */
  getReportId() {
    return get(this.reportAssignments, [OwnerType.user, 'id']);
  }

  /**
   * Get the report template ID.
   *
   * @returns {string}
   */
  getTemplateId() {
    return this.templateId;
  }

  /**
   * Get the localized report name (title).
   *
   * @todo Localized value from report settings
   *
   * @param {string} locale - The locale key, e.g. "en-UK", "fr-CA"
   * @returns {string}
   */
  getName(locale) {
    return getLocalizedValue({ localizedValues: this.name, locale });
  }

  /**
   * Get the localized report description.
   *
   * @todo Localized value from report settings
   *
   * @param {string} locale - The locale key, e.g. "en-UK", "fr-CA"
   * @returns {string}
   */
  getDescription({ locale } = {}) {
    return getLocalizedValue({ localizedValues: this.description, locale });
  }

  /**
   * Get raw ES query.
   *
   * @param {boolean} [clone=true]
   * @returns {Object}
   */
  getQuery({ clone = true } = {}) {
    return clone ? cloneDeep(this.query) : this.query;
  }

  /**
   * Get the user real-time notifications.
   * @param {boolean} [clone=true]
   * @returns {{id: string|undefined, isPushNotificationsEnabled: boolean|undefined, isSMSNotificationsEnabled: boolean|undefined}}
   */
  getUserNotifications({ clone = true } = {}) {
    return clone ? cloneDeep(this.userNotification) : this.userNotification;
  }

  /**
   * Get the report version number.
   * @returns {number}
   */
  getReportVersion() {
    return get(this.metadata, 'version');
  }

  /**
   * Check whether this is an old report.
   * @returns {boolean}
   */
  isOldReport() {
    return this.getReportVersion() < CURRENT_REPORT_VERSION;
  }

  /**
   * Check if this is a default report. (Note: Currently this means it checks whether it is a Solink report.)
   *
   * @todo Might need to add a report setting to indicate this too (used for ordering)
   *
   * @returns {boolean}
   */
  isDefaultReport() {
    return this.templateOwnerType === OwnerType.solink;
  }

  /**
   * Check if the report belongs to Solink, the cloud or the reseller.
   *
   * @returns {boolean}
   */
  isSystemReport() {
    const systemOwners = [OwnerType.solink, OwnerType.cloud, OwnerType.reseller];
    return systemOwners.includes(this.templateOwnerType);
  }

  /**
   * Check if the report belongs to the customer.
   *
   * @returns {boolean}
   */
  isCustomerReport() {
    return this.templateOwnerType === OwnerType.customer;
  }

  /**
   * Check if the report belongs to the user.
   *
   * @returns {boolean}
   */
  isUserReport() {
    return this.templateOwnerType === OwnerType.user;
  }

  /**
   * Check if the report has a user assignment (i.e. custom settings for the user).
   *
   * @returns {boolean}
   */
  hasUserAssignment() {
    return !isEmpty(this.reportAssignments[OwnerType.user]);
  }

  /**
   * Check if the report is a filter type.
   *
   * @returns {boolean}
   */
  isFilter() {
    return this.reportType === 'filter';
  }

  /**
   * Check if the report is an aggregation type.
   *
   * @returns {boolean}
   */
  isAggregation() {
    return this.reportType === 'aggregation';
  }

  /**
   * @todo Temp way of identify aggregations meant for digest emails
   * @returns {boolean}
   */
  isDigestOnly() {
    return get(this.reportSettings, 'digest.render.type') === 'scorecard';
  }

  /**
   * Indicates whether a report can have real-time notifications.
   *
   * @returns {boolean}
   */
  canSetRealTime() {
    return this.isFilter() && !this.isOldReport();
  }

  /**
   * Indicates whether a report can have digest notifications.
   *
   * @returns {boolean}
   */
  canSetDigest() {
    return (
      this.isFilter() ||
      // Only aggregations that are rendered as scorecards (metric by date) is supported in digests right now
      (this.isAggregation() && this.isDigestOnly())
    );
  }

  /**
   * Indicates whether a report's visibility can be changed.
   *
   * @returns {boolean}
   */
  canSetVisibility() {
    return (
      this.isFilter() ||
      // TODO Investigate rendering of digest aggregations in VD
      (this.isAggregation() && !this.isDigestOnly())
    );
  }

  /**
   * Get the visibility.
   *
   * @returns {boolean|null}
   */
  getVisibility() {
    return this.visibility;
  }

  /**
   * Check whether the report should be visible.
   *
   * @returns {boolean}
   */
  isVisible() {
    let visibility = this.getVisibility();

    if (isNil(visibility)) {
      // TODO Visibility should also be part of inheritance
      // Old reports (version 1 at time of coding) are hidden by default, while all other reports a user can access will be shown automatically
      visibility = !this.isOldReport();
    }

    // Right now if you can't toggle visibility it also means the report shouldn't be visible in VD
    return visibility && this.canSetVisibility();
  }

  /**
   * Check whether the report is set for daily digests.
   *
   * @returns {boolean}
   */
  hasDailyDigest() {
    return get(this.reportSettings, 'digest.notifications.hasDaily', false);
  }

  /**
   * Check whether the report is set for weekly digests.
   * @returns {boolean}
   */
  hasWeeklyDigest() {
    return get(this.reportSettings, 'digest.notifications.hasWeekly', false);
  }

  /**
   * Check whether the report has any digest notification set.
   *
   * @returns {boolean}
   */
  hasDigestNotifications() {
    return this.hasDailyDigest() || this.hasWeeklyDigest();
  }

  /**
   * Check whether the report is set for push notifications.
   *
   * @returns {boolean}
   */
  hasPushNotification() {
    return get(this.userNotification, 'isPushNotificationsEnabled', false);
  }

  /**
   * Check whether the report is set for SMS notifications.
   *
   * @returns {boolean}
   */
  hasSMSNotification() {
    return get(this.userNotification, 'isSMSNotificationsEnabled', false);
  }

  /**
   * Check whether the report is set for email notifications.
   *
   * @returns {boolean}
   */
  hasEmailNotification() {
    return get(this.userNotification, 'isEmailNotificationsEnabled', false);
  }

  /**
   * Check whether the report is set for any real-time notification.
   *
   * @returns {boolean}
   */
  hasRealTimeNotifications() {
    return this.hasPushNotification() || this.hasSMSNotification() || this.hasEmailNotification();
  }

  /**
   * Check whether the user can delete the report.
   *
   * @returns {boolean}
   */
  canDelete() {
    return this.isUserReport();
  }

  /**
   * Generate digest-related parameters (for API calls).
   *
   * @param {boolean} [hasDaily]
   * @param {boolean} [hasWeekly]
   * @returns {{hasDigest: boolean, reportSettings: Object}}
   */
  generateDigestParameters({ hasDaily, hasWeekly }) {
    // Check if the user already has custom report settings
    const settings = get(this.reportAssignments, [OwnerType.user, 'reportSettings']);
    const userReportSettings = settings ? cloneDeep(settings) : {};

    return ReportUtil.generateReportSettings({ userReportSettings, hasDaily, hasWeekly });
  }

  /**
   * Merge user report data (after update).
   *
   * @param {Object} [report]
   */
  mergeUserReportData({ report }) {
    if (report.ownerType !== OwnerType.user) {
      throw new Error('ReportUtil: Only user report (assignments)');
    }

    this.reportAssignments[OwnerType.user] = cloneDeep(report);

    // Handle merge properties

    const { isVisible, reportSettings, queryParams } = report;

    if (isVisible !== undefined) {
      this.visibility = isVisible;
    }

    if (reportSettings !== undefined) {
      this.reportSettings = merge(this.reportSettings, reportSettings);
    }

    if (queryParams !== undefined) {
      this.queryParams = merge(this.queryParams, queryParams);
    }
  }

  /**
   * Merge report template data (after update).
   *
   * @param {Object} [name]
   * @param {Object} [description]
   */
  mergeTemplateData({ name, description }) {
    if (name !== undefined) {
      this.name = name;
    }

    if (description !== undefined) {
      this.description = description;
    }
  }

  /**
   * Merge user notifications data (after update).
   *
   * @param {Object} userNotification
   */
  mergeUserNotificationData({ userNotification }) {
    this.userNotification = cloneDeep(userNotification);
  }

  /**
   * Remove/clear user notifications (after delete).
   */
  removeUserNotification() {
    this.userNotification = {};
  }

  /**
   * Add a report assignment.
   * @param {string} id - The report (assignment) ID
   * @param {string} ownerType - The report (assignment) owner
   * @param {boolean} isVisible
   * @param {Object} queryParams
   * @param {Object} reportSettings
   */
  parseAssignment({ id, ownerType, isVisible, queryParams, reportSettings }) {
    this.reportAssignments[ownerType] = {
      id,
      ownerType,
      isVisible,
      queryParams,
      reportSettings,
    };
  }

  static getReportUtilInputs({ reportTemplate, rootReport }) {
    return {
      templateId: reportTemplate.id,
      templateOwnerType: reportTemplate.ownerType,
      reports: reportTemplate.reports,
      reportType: reportTemplate.reportType,
      name: reportTemplate.name,
      description: reportTemplate.description,
      queryType: reportTemplate.queryType,
      query: reportTemplate.query,
      metadata: reportTemplate.reportMetadata,
      // Merged properties
      // TODO long term this won't work - need merged settings from CH
      isVisible: get(reportTemplate, 'mergedReport.isVisible', get(rootReport, 'isVisible', true)),
      queryParams: get(
        reportTemplate,
        'mergedReport.queryParams',
        get(rootReport, 'queryParams', {}),
      ),
      reportSettings: get(
        reportTemplate,
        'mergedReport.reportSettings',
        get(rootReport, 'reportSettings', {}),
      ),
      // User notifications (real-time notifications)
      userNotification: get(reportTemplate, 'userNotifications.0', {}),
    };
  }

  /**
   * Parse reports from API response.
   *
   * @param {Array<unknown>} reportTemplates - The user's report templates
   * @returns {Array<ReportUtil>}
   */
  static parseReports(reportTemplates) {
    const reportHelpers = [];
    for (const reportTemplate of reportTemplates) {
      const helper = new ReportUtil(ReportUtil.getReportUtilInputs({ reportTemplate }));

      for (const report of reportTemplate.reports) {
        helper.parseAssignment({ ...report });
      }

      reportHelpers.push(helper);
    }

    return reportHelpers;
  }

  /**
   * Parse newly created report from API response.
   *
   * @param {Object} reportTemplate
   * @param {Object} rootReport
   * @returns {ReportUtil}
   */
  static parseNewReport({ reportTemplate, rootReport }) {
    const helper = new ReportUtil(ReportUtil.getReportUtilInputs({ reportTemplate, rootReport }));

    helper.parseAssignment({ ...rootReport });

    return helper;
  }

  /**
   * Generate the report settings. Intended for a user owned report (assignment).
   *
   * @param {Object} [userReportSettings]
   * @param {boolean} [hasDaily]
   * @param {boolean} [hasWeekly]
   * @returns {{hasDigest: boolean, reportSettings: Object}}
   */
  static generateReportSettings({ userReportSettings = {}, hasDaily, hasWeekly }) {
    // The options have to be explicitly set to false or true (otherwise current value assumed)

    if (hasDaily !== undefined) {
      set(userReportSettings, 'digest.notifications.hasDaily', hasDaily);
    }

    if (hasWeekly !== undefined) {
      set(userReportSettings, 'digest.notifications.hasWeekly', hasWeekly);
    }

    const hasDigest =
      get(userReportSettings, 'digest.notifications.hasDaily') ||
      get(userReportSettings, 'digest.notifications.hasWeekly');

    return {
      hasDigest: hasDigest || false,
      reportSettings: userReportSettings,
    };
  }
}

export default ReportUtil;
